import React, { useEffect, useMemo, useState } from 'react';
import {
    Clock3,
    FileText,
    History,
    RefreshCw,
    Save,
    X,
    Eye,
    RotateCcw
} from 'lucide-react';

const API_BASE = 'https://agent.gabrielny.com/emberkit';

const AdminTemplatesPage = () => {
    const [files, setFiles] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [contents, setContents] = useState({});
    const [originalContents, setOriginalContents] = useState({});
    const [loading, setLoading] = useState(true);

    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyItems, setHistoryItems] = useState([]);
    const [previewContent, setPreviewContent] = useState('');
    const [previewName, setPreviewName] = useState('');
    const [toast, setToast] = useState(null);

    useEffect(() => {
        loadEditableFiles();
    }, []);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        window.clearTimeout(window.__adminToastTimer);
        window.__adminToastTimer = window.setTimeout(() => {
            setToast(null);
        }, 2800);
    };

    const loadEditableFiles = async () => {
        try {
            setLoading(true);

            const res = await fetch(`${API_BASE}/admin/files`);
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || 'Failed to load files');
            }

            const names = data.files || [];
            const nextContents = {};
            const nextOriginals = {};

            await Promise.all(
                names.map(async (filename) => {
                    const fileRes = await fetch(`${API_BASE}/admin/file-content?filename=${encodeURIComponent(filename)}`);
                    const fileData = await fileRes.json();

                    if (!fileRes.ok) {
                        throw new Error(fileData.detail || `Failed to load ${filename}`);
                    }

                    nextContents[filename] = fileData.content || '';
                    nextOriginals[filename] = fileData.content || '';
                })
            );

            setFiles(names);
            setContents(nextContents);
            setOriginalContents(nextOriginals);
            setActiveFile(names[0] || null);
        } catch (err) {
            console.error(err);
            showToast(err.message || 'Failed to load admin files', 'error');
        } finally {
            setLoading(false);
        }
    };

    const activeContent = useMemo(() => {
        if (!activeFile) return '';
        return contents[activeFile] ?? '';
    }, [activeFile, contents]);

    const handleChangeContent = (value) => {
        if (!activeFile) return;
        setContents((prev) => ({
            ...prev,
            [activeFile]: value,
        }));
    };

    const handleReset = () => {
        if (!activeFile) return;
        setContents((prev) => ({
            ...prev,
            [activeFile]: originalContents[activeFile] || '',
        }));
        showToast(`${activeFile} reset to loaded version`);
    };

    const handleSave = async () => {
        if (!activeFile) return;

        try {
            const res = await fetch(`${API_BASE}/admin/save-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filename: activeFile,
                    content: contents[activeFile] || '',
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || data.message || 'Save failed');
            }

            setOriginalContents((prev) => ({
                ...prev,
                [activeFile]: contents[activeFile] || '',
            }));

            showToast(data.message || `${activeFile} saved successfully`, 'success');
        } catch (err) {
            console.error(err);
            showToast(err.message || 'Save failed', 'error');
        }
    };

    const openHistory = async () => {
        if (!activeFile) return;

        try {
            setHistoryOpen(true);
            setHistoryLoading(true);
            setPreviewContent('');
            setPreviewName('');

            const res = await fetch(`${API_BASE}/admin/backups?filename=${encodeURIComponent(activeFile)}`);
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || 'Failed to load backups');
            }

            setHistoryItems(data.backups || []);
        } catch (err) {
            console.error(err);
            showToast(err.message || 'Failed to load history', 'error');
            setHistoryItems([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const previewBackup = async (backupName) => {
        if (!activeFile) return;

        try {
            const res = await fetch(
                `${API_BASE}/admin/backup-content?filename=${encodeURIComponent(activeFile)}&backup_name=${encodeURIComponent(backupName)}`
            );
            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || data.message || 'Preview failed');
            }

            setPreviewName(backupName);
            setPreviewContent(data.content || '');
        } catch (err) {
            console.error(err);
            showToast(err.message || 'Preview failed', 'error');
        }
    };

    const restoreBackup = async (backupName) => {
        if (!activeFile) return;

        const ok = window.confirm(
            `Restore ${activeFile} from backup?\n\n${backupName}\n\nYour current live version will be backed up first.`
        );
        if (!ok) return;

        try {
            const res = await fetch(`${API_BASE}/admin/restore-template`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filename: activeFile,
                    backup_name: backupName,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || data.message || 'Restore failed');
            }

            showToast(data.message || 'Backup restored successfully', 'success');
            await loadEditableFiles();
            setHistoryOpen(false);
        } catch (err) {
            console.error(err);
            showToast(err.message || 'Restore failed', 'error');
        }
    };

    const isDirty = activeFile && contents[activeFile] !== originalContents[activeFile];

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mx-auto max-w-7xl">
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-gray-900">Ember Admin Panel</h1>
                        <p className="mt-2 text-sm text-gray-500">
                            Edit live prompt/template files, save instantly, and restore previous versions.
                        </p>

                        <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Backups kept: last 50 per file
              </span>
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                Cache clear: automatic on save
              </span>
                        </div>
                    </div>

                    <button
                        onClick={loadEditableFiles}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                        <RefreshCw size={16} />
                        Reload
                    </button>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
                    <aside className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="mb-3 flex items-center gap-2">
                            <FileText size={16} className="text-gray-500" />
                            <h2 className="text-sm font-semibold text-gray-700">Editable text files</h2>
                        </div>

                        <div className="space-y-2">
                            {files.map((file) => {
                                const dirty = contents[file] !== originalContents[file];

                                return (
                                    <button
                                        key={file}
                                        onClick={() => setActiveFile(file)}
                                        className={[
                                            'w-full rounded-xl border px-4 py-3 text-left transition',
                                            activeFile === file
                                                ? 'border-blue-200 bg-blue-50'
                                                : 'border-gray-200 bg-white hover:bg-gray-50'
                                        ].join(' ')}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="break-all text-sm font-medium text-gray-800">{file}</span>
                                            {dirty && (
                                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                          Unsaved
                        </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}

                            {!loading && files.length === 0 && (
                                <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                                    No editable files found.
                                </div>
                            )}
                        </div>
                    </aside>

                    <main className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                        {loading ? (
                            <div className="py-20 text-center text-sm text-gray-500">Loading admin files...</div>
                        ) : !activeFile ? (
                            <div className="py-20 text-center text-sm text-gray-500">No file selected.</div>
                        ) : (
                            <>
                                <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900">{activeFile}</h3>
                                        <p className="mt-1 text-sm text-gray-500">Editing live prompt/template file</p>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={openHistory}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                                        >
                                            <History size={16} />
                                            History
                                        </button>

                                        <button
                                            onClick={handleReset}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                                        >
                                            <RotateCcw size={16} />
                                            Reset
                                        </button>

                                        <button
                                            onClick={handleSave}
                                            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                        >
                                            <Save size={16} />
                                            Save Changes
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-3 flex items-center justify-between text-xs text-gray-500">
                                    <span>{isDirty ? 'You have unsaved changes' : 'All changes saved'}</span>
                                    <span className="inline-flex items-center gap-1">
                    <Clock3 size={12} />
                    Live template editor
                  </span>
                                </div>

                                <textarea
                                    spellCheck={false}
                                    value={activeContent}
                                    onChange={(e) => handleChangeContent(e.target.value)}
                                    className="min-h-[70vh] w-full rounded-2xl border border-gray-200 bg-white p-4 font-mono text-sm leading-6 text-gray-900 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                                />
                            </>
                        )}
                    </main>
                </div>
            </div>

            {historyOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setHistoryOpen(false)}
                >
                    <div
                        className="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900">EmberGit: Version History</h3>
                                <p className="mt-1 text-sm text-gray-500">{activeFile}</p>
                            </div>

                            <button
                                onClick={() => setHistoryOpen(false)}
                                className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {historyLoading ? (
                            <div className="py-10 text-sm text-gray-500">Loading backups...</div>
                        ) : historyItems.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
                                No backups found for this file.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {historyItems.map((item) => (
                                    <div
                                        key={item.backup_name}
                                        className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                                    >
                                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                            <div>
                                                <div className="font-medium text-gray-900">{item.backup_name}</div>
                                                <div className="mt-1 text-xs text-gray-500">
                                                    Saved: {item.created_at_utc} · {item.size_bytes} bytes
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => previewBackup(item.backup_name)}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
                                                >
                                                    <Eye size={14} />
                                                    Preview
                                                </button>

                                                <button
                                                    onClick={() => restoreBackup(item.backup_name)}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
                                                >
                                                    <RotateCcw size={14} />
                                                    Restore
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {previewName && (
                            <div className="mt-6 border-t border-gray-200 pt-4">
                                <div className="mb-3">
                                    <div className="text-lg font-semibold text-gray-900">Preview</div>
                                    <div className="text-sm text-gray-500">{previewName}</div>
                                </div>

                                <textarea
                                    readOnly
                                    spellCheck={false}
                                    value={previewContent}
                                    className="min-h-[32vh] w-full rounded-2xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm leading-6 text-gray-900"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {toast && (
                <div
                    className={[
                        'fixed bottom-6 right-6 z-[60] rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg',
                        toast.type === 'error' ? 'bg-red-600' : 'bg-gray-900'
                    ].join(' ')}
                >
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default AdminTemplatesPage;